import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import * as d3 from 'd3';
import { ResizableTable } from '../../components/resizableTable/ResizableTable';
import SalesForecastCurrentQuarterTable, { buildForecastColumnsFromConfig } from '../RegionalSalesReviewPackage/SalesForecastCurrentQuarterTable';
import { dateFormatterMomentV3 } from '../../util/customFunctions';
import { flexToWidth } from '../../util/utils';

// Helper: map text length to pixel column width
const calculateColumnWidth = (textLength) => {
  const cappedLength = Math.min(textLength, 80);
  if (cappedLength <= 2) return 48;
  if (cappedLength <= 5) return 60;
  if (cappedLength <= 10) return 80;
  if (cappedLength <= 20) return 120;
  if (cappedLength <= 30) return 180;
  if (cappedLength <= 45) return 240;
  if (cappedLength <= 60) return 352;
  if (cappedLength <= 80) return 500;
  return 120;
};

// Helper: calculate default width for a column based on row data
const calculateDefaultWidthForField = (field, rows, getCellValue) => {
  let maxLength = field.length;
  if (rows && rows.length > 0) {
    rows.slice(0, 20).forEach((row) => {
      let value = row[field];
      if (getCellValue) {
        const formatted = getCellValue(row, field);
        value = formatted != null ? String(formatted) : '';
      } else {
        value = value != null ? String(value) : '';
      }
      maxLength = Math.max(maxLength, Math.min(String(value || '').trim().length, 40));
    });
  }
  return calculateColumnWidth(Math.min(maxLength, 40));
};

// Build ResizableTable columns from LKP_DRILLDOWN_CONFIG records.
// Returns null if configColumns is empty (caller falls back to static columns).
const buildColumnsFromConfig = (configColumns = [], rows = [], getCellValue = null, currencySymbol = '') => {
  if (!configColumns || configColumns.length === 0) return null;

  const columns = [
    {
      label: '#',
      sortable: false,
      subColumns: [{
        label: '#',
        field: 'rowNum',
        type: 'number',
        align: 'center',
        width: 10,
        sortable: false,
      }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    },
  ];

  configColumns.forEach((col) => {
    let type = 'text';
    let align = 'left';
    if (col.type === 'currency' || col.type === 'integer') {
      type = 'number';
      align = 'right';
    } else if (col.type === 'percent') {
      type = 'percentage';
      align = 'right';
    } else if (col.type === 'date' || col.type === 'datetime') {
      type = 'date';
      align = 'right';
    }

    const width = col.flex != null
      ? flexToWidth(col.flex)
      : calculateDefaultWidthForField(col.field, rows, getCellValue);

    columns.push({
      label: col.label,
      sortable: true,
      subColumns: [{
        label: col.label,
        field: col.field,
        type,
        align,
        width,
        sortable: true,
        filterable: true,
      }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    });
  });

  return columns;
};

const createSummaryTableColumns = (currencySymbol = '') => [
  {
    label: '',
    sortable: false,
    subColumns: [{
      label: '',
      field: 'label',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: '# of Opps',
    sortable: false,
    subColumns: [{
      label: '# of Opps',
      field: 'count',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}M)`,
    sortable: false,
    subColumns: [{
      label: `ACV (${currencySymbol}M)`,
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

const createForecastOppsColumns = () => [
  {
    label: '#',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'left',
      width: 20,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opportunity',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Opportunity',
      field: 'opportunity',
      type: 'text',
      align: 'left',
      width: 115,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Owner',
      field: 'owner',
      type: 'text',
      align: 'left',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Exec. Spon.',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Executive Sponsor',
      field: 'Executive_Sponsor',
      type: 'text',
      align: 'left',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  // {
  //   label: 'Strategic Account',
  //   sortable: true,
  //   align: 'center',
  //   subColumns: [{
  //     label: 'Strategic Account',
  //     field: 'Strategic_Account',
  //     type: 'text',
  //     align: 'left',
  //     width: 30,
  //     sortable: true,
  //     filterable: true,
  //   }],
  //   headerBgColor: '#1976d2',
  //   headerTextColor: '#ffffff',
  // },
  {
    label: 'Cust. Type',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Cust. Type',
      field: 'Cust_Type',
      type: 'text',
      align: 'left',
      width: 37,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Lead Source',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Lead Source',
      field: 'Lead_Source',
      type: 'text',
      align: 'left',
      width: 37,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  
  {
    label: 'ACV ($K)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'ACV ($K)',
      field: 'acvInK',
      type: 'number',
      align: 'right',
      width: 35,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Disc. %',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Disc. %',
      field: 'discountPercent',
      type: 'number',
      align: 'left',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Proj. Close Date',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Proj. Close Date',
      field: 'projectedCloseDate',
      type: 'date',
      align: 'right',
      width: 44,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Stage',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Stage',
      field: 'stageChar',
      type: 'text',
      align: 'center',
      width: 25,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Days in Stg.',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Days in Stg.',
      field: 'daysInStage',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Forecast Category',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Forecast Category',
      field: 'forecastCategory',
      type: 'text',
      align: 'left',
      width: 50,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Partner',
      field: 'partner',
      type: 'text',
      align: 'left',
      width: 70,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risks',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: 'Risks',
      field: 'risks',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Actions',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: 'Actions',
      field: 'actions',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Transform opportunity data to rows with both legacy and LKP_DRILLDOWN_CONFIG field names
const transformOpportunityDataToRows = (opps, includeTotal = false, totalData = null) => {
  const rows =
    opps?.map((opp) => ({
      rowNum: opp.rowNum,
      // Legacy field names
      opportunity: opp.opportunity,
      owner: opp.owner,
      executiveSponsor: opp.executiveSponsor || opp.Executive_Sponsor || '',
      Executive_Sponsor: opp.Executive_Sponsor || opp.executiveSponsor || '',
      acvInK: opp.acvInK,
      discountPercent: opp.discountPercent != null ? opp.discountPercent : null,
      projectedCloseDate: opp.projectedCloseDate,
      ageSinceQualDays: opp.ageSinceQualDays,
      stageChar: opp.stageChar,
      daysInStage: opp.daysInStage != null ? opp.daysInStage : null,
      partner: opp.partner,
      Lead_Source: opp.Lead_Source || '',
      Strategic_Account: opp.Strategic_Account ?? '',
      forecastCategory: opp.forecastCategory,
      nextStep: opp.nextStep,
      isNewThisWeek: opp.isNewThisWeek,
      risks: opp.risks || '',
      actions: opp.actions || '',
      // LKP_DRILLDOWN_CONFIG field names
      OppName: opp.opportunity,
      Owner_Name: opp.owner,
      ACV: opp.acv != null ? opp.acv : (opp.acvInK != null ? opp.acvInK * 1000 : null),
      Projected_Close_Date: opp.projectedCloseDate,
      Days_Since_Qualified: opp.ageSinceQualDays,
      Stage: opp.stageChar,
      Days_at_Stage: opp.daysInStage != null ? opp.daysInStage : null,
      Forecast_Category_Name: opp.forecastCategory,
      Partner_Name: opp.partner,
      RISK__C: opp.risks || '',
      ACTION__C: opp.actions || '',
      Cust_Type: opp.Cust_Type || '',
      // Drilldown identifiers
      oppId: opp.oppId || opp.Opp_ID || null,
      Opp_ID: opp.Opp_ID || opp.oppId || null,
      label: opp.opportunity || '',
    })) || [];

  if (includeTotal && totalData) {
    rows.push({
      rowNum: null,
      opportunity: `Total (${totalData.totalCount} opps)`,
      OppName: `Total (${totalData.totalCount} opps)`,
      owner: null, Owner_Name: null,
      executiveSponsor: null, Executive_Sponsor: null,
      acvInK: totalData.totalACVInK,
      ACV: totalData.totalACVInK != null ? totalData.totalACVInK * 1000 : null,
      discountPercent: null,
      projectedCloseDate: null, Projected_Close_Date: null,
      ageSinceQualDays: null, Days_Since_Qualified: null,
      stageChar: null, Stage: null,
      daysInStage: null, Days_at_Stage: null,
      partner: null, Partner_Name: null,
      Lead_Source: null,
      Strategic_Account: null,
      forecastCategory: null, Forecast_Category_Name: null,
      nextStep: null,
      isNewThisWeek: false,
      risks: '', RISK__C: '',
      actions: '', ACTION__C: '',
      Cust_Type: '',
      label: 'Total',
    });
  }

  return rows;
};

const RegionalRSMSalesForecastSection = ({
  classes,
  salesForecastCurrentQuarterData,
  isSalesForecastCurrentQuarterLoading,
  forecastMovedOutData,
  isForecastMovedOutLoading,
  forecastBestCaseData,
  isForecastBestCaseLoading,
  salesForecastNextQuarterData,
  isSalesForecastNextQuarterLoading,
  forecastOpportunityColumns,
  wonOpportunityColumns = [],
  currencySymbol = '',
  enableDrilldown = true,
}) => {
  const salesForecastCurrentQuarter = salesForecastCurrentQuarterData || null;
  // Same cell value formatter as RegionalSalesReviewPackage for forecast opportunity rows (raw DB field names)
  const forecastGetCellValue = useCallback((row, field, rowIndex) => {
    if (field === 'rowNum') {
      if (row.isTotal) return '-';
      return rowIndex != null ? rowIndex + 1 : (row[field] ?? '');
    }
    if (field === 'Projected_Close_Date_At_Time' || field === 'Projected_Close_Date') {
      const val = row[field];
      if (!val) return '';
      try { return dateFormatterMomentV3(val); } catch (_) { return ''; }
    }
    if (field === 'Acv_At_Time_T' || field === 'ACV' || field === 'acvInK') {
      let val = row[field] ?? row.ACV ?? row.Acv_At_Time_T;
      if (val != null && field !== 'acvInK') {
        return d3.format(',.0f')(val / 1000);
      }
      if (row.acvInK != null) {
        return d3.format(',.0f')(row.acvInK);
      }
      return '';
    }
    if (field === 'Days_Since_Qualified' || field === 'Days_at_Stage') {
      return row[field] != null ? d3.format(',')(row[field]) : '';
    }
    return row[field] != null ? row[field] : '';
  }, []);

  // Cell value formatter handling both legacy and LKP field names (for transformed rows when config is missing)
  const getCellValue = useCallback((row, field, rowIndex) => {
    if (field === 'rowNum') {
      if (row.label === 'Total' || row.label === 'Quota' || row.label === 'Other Opps') {
        return row[field] != null ? row[field] : null;
      }
      return rowIndex != null ? rowIndex + 1 : (row[field] != null ? row[field] : '');
    }
    // Legacy field formatting
    if (field === 'projectedCloseDate') return row[field] ? dateFormatterMomentV3(row[field]) : '';
    if (field === 'acvInK' && row[field] != null) return d3.format(",.0f")(row[field]);
    if (field === 'ageSinceQualDays' && row[field] != null) return d3.format(",")(row[field]);
    if (field === 'daysInStage' && row[field] != null) return d3.format(",")(row[field]);
    // LKP_DRILLDOWN_CONFIG field formatting
    if (field === 'Projected_Close_Date') return row[field] ? dateFormatterMomentV3(row[field]) : '';
    if (field === 'ACV' && row[field] != null) return d3.format(",.0f")(row[field] / 1000);
    if (field === 'Days_Since_Qualified' && row[field] != null) return d3.format(",")(row[field]);
    if (field === 'Days_at_Stage' && row[field] != null) return d3.format(",")(row[field]);
    if (field === 'Executive_Sponsor' || field === 'executiveSponsor') {
      return row.Executive_Sponsor != null ? row.Executive_Sponsor : (row.executiveSponsor != null ? row.executiveSponsor : '');
    }
    if (row[field] == null || row[field] === '') return '';
    return row[field];
  }, []);

  const getCellStyle = useCallback((row) => {
    if (row.label === 'Total') return { fontWeight: 600, backgroundColor: '#f5f5f5' };
    if (row.isNewThisWeek) return { backgroundColor: '#c8e6c9' };
    return {};
  }, []);

  // Forecast Moved Out: use raw movedOutOpps (same as RegionalSalesReviewPackage ForecastMovedOutPaper)
  const forecastMovedOutRawRows = useMemo(() => forecastMovedOutData?.movedOutOpps || [], [forecastMovedOutData]);

  // Combine raw rows and total row for rendering
  const forecastMovedOutRowsWithTotal = useMemo(() => {
    if (forecastMovedOutRawRows.length === 0) return forecastMovedOutRawRows;
    const totalCount = forecastMovedOutData?.totalCount;
    const totalACVInK = forecastMovedOutData?.totalACVInK;
    return [
      ...forecastMovedOutRawRows,
      {
        isTotal: true,
        opportunity: `Total (${totalCount} opps)`,
        OppName: `Total (${totalCount} opps)`,
        acvInK: totalACVInK
      }
    ];
  }, [forecastMovedOutRawRows, forecastMovedOutData]);

  // Forecast Moved Out columns: LKP config (same as SalesForecastCurrentQuarterTable)
  const forecastMovedOutColumns = useMemo(() => {
    return buildForecastColumnsFromConfig(forecastOpportunityColumns) || createForecastOppsColumns();
  }, [forecastOpportunityColumns]);

  const forecastBestCaseRows = useMemo(() => {
    const opps = forecastBestCaseData?.bestCaseOpps || [];
    const hasTotals =
      forecastBestCaseData &&
      forecastBestCaseData.totalCount != null &&
      forecastBestCaseData.totalACVInK != null &&
      opps.length > 0;
    const totalData = hasTotals
      ? {
          totalCount: forecastBestCaseData.totalCount,
          totalACVInK: forecastBestCaseData.totalACVInK,
        }
      : null;
    return transformOpportunityDataToRows(opps, !!totalData, totalData);
  }, [forecastBestCaseData]);

  // Sales Forecast Next Quarter: full forecastOpps + total + quota rows (same as SalesForecastNextQtrPaper)
  const salesForecastNextQuarterOppsWithSummary = useMemo(() => {
    const forecastOpps = salesForecastNextQuarterData?.forecastOpps || [];
    const totalACV = forecastOpps.reduce((sum, row) => {
      const val = row.Acv_At_Time_T ?? row.ACV ?? 0;
      return sum + (typeof val === 'number' ? val : 0);
    }, 0);
    const quotaRaw =
      salesForecastNextQuarterData?.summary?.quotaInMillions != null
        ? parseFloat(salesForecastNextQuarterData.summary.quotaInMillions) * 1_000_000
        : null;

    if (forecastOpps.length === 0) return forecastOpps;
    return [
      ...forecastOpps,
      {
        isTotal: true,
        Opp_ID: '__total__',
        OppName: `Total (${forecastOpps.length} opps)`,
        Acv_At_Time_T: totalACV,
        ACV: totalACV,
      },
      ...(quotaRaw != null
        ? [{
            isQuota: true,
            Opp_ID: '__quota__',
            OppName: 'Quota',
            Acv_At_Time_T: quotaRaw,
            ACV: quotaRaw,
          }]
        : []),
    ];
  }, [salesForecastNextQuarterData]);

  // Sales Forecast Next Quarter columns: LKP config (same as SalesForecastCurrentQuarterTable)
  const salesForecastNextQuarterColumns = useMemo(() => {
    return buildForecastColumnsFromConfig(forecastOpportunityColumns) || createForecastOppsColumns();
  }, [forecastOpportunityColumns]);

  const getNextQtrCellValue = useCallback((row, field, rowIndex) => {
    if (row.isTotal || row.isQuota) {
      if (field === 'rowNum') return '-';
      if (field === 'OppName') return row.OppName;
      if (field === 'Acv_At_Time_T' || field === 'ACV') {
        const val = row[field];
        return val != null ? d3.format(',.0f')(val / 1000) : '';
      }
      return '';
    }
    return forecastGetCellValue(row, field, rowIndex);
  }, [forecastGetCellValue]);

  const getNextQtrCellStyle = useCallback((row) => {
    if (row.isTotal) return { backgroundColor: '#f5f5f5', fontWeight: 700 };
    if (row.isQuota) return { backgroundColor: '#fff9c4', fontWeight: 700 };
    if (row.Is_New_This_Week === 'Y') return { backgroundColor: '#c8e6c9' };
    return {};
  }, []);

  // Sales Forecast Next Quarter summary rows (for Summary table above opps table)
  const salesForecastNextQuarterSummaryRows = useMemo(() => [
    {
      label: 'Won QTD',
      count: salesForecastNextQuarterData?.summary?.won?.count || 0,
      acvInMillions: salesForecastNextQuarterData?.summary?.won?.acvInMillions || '0.00',
      rowType: 'won',
    },
    {
      label: 'Most Likely + Commit',
      count: salesForecastNextQuarterData?.summary?.forecast?.count || 0,
      acvInMillions: salesForecastNextQuarterData?.summary?.forecast?.acvInMillions || '0.00',
      rowType: 'forecast',
    },
    {
      label: 'Total Forecast',
      count: salesForecastNextQuarterData?.summary?.total?.count || 0,
      acvInMillions: salesForecastNextQuarterData?.summary?.total?.acvInMillions || '0.00',
      rowType: 'total',
    },
    {
      label: 'Forecast Goal (1.5x Quota)',
      count: '',
      acvInMillions: salesForecastNextQuarterData?.summary?.forecastGoal || '0.00',
      rowType: 'forecast',
    },
    {
      label: 'Quota',
      count: '',
      acvInMillions: salesForecastNextQuarterData?.summary?.quotaInMillions || '0.00',
      rowType: 'quota',
    },
  ], [salesForecastNextQuarterData]);

  return (
    <>
      <SalesForecastCurrentQuarterTable
        classes={classes}
        data={salesForecastCurrentQuarter}
        isLoading={isSalesForecastCurrentQuarterLoading}
        asOfDateLabel=""
        handleChangeLocation={() => {}}
        enableDrilldown={enableDrilldown}
        forecastOpportunityColumns={forecastOpportunityColumns}
        wonOpportunityColumns={wonOpportunityColumns}
        currencySymbol={currencySymbol}
      />

      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              Sales Forecast – Current Quarter – Forecast – Most Likely + Commit Opps moved out in the last week
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isForecastMovedOutLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {/* Summary — only show when there are moved-out opps (same as ForecastMovedOutPaper) */}
              {/*
              {forecastMovedOutRawRows.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Summary
                  </Typography>
                  <ResizableTable
                    columns={createSummaryTableColumns(currencySymbol)}
                    rows={[
                      {
                        label: 'Moved Out Opps',
                        count: forecastMovedOutData?.summary?.movedOut?.count ?? forecastMovedOutData?.totalCount ?? 0,
                        acvInMillions: forecastMovedOutData?.summary?.movedOut?.acvInMillions ?? forecastMovedOutData?.totalACVInMillions ?? '0.00',
                        rowType: 'movedOut',
                      },
                      {
                        label: 'Forecast Goal (1.5x Quota)',
                        count: '',
                        acvInMillions: forecastMovedOutData?.summary?.forecastGoal ?? forecastMovedOutData?.forecastGoal ?? '0.00',
                        rowType: 'forecast',
                      },
                      {
                        label: 'Quota',
                        count: '',
                        acvInMillions: forecastMovedOutData?.summary?.quotaInMillions ?? forecastMovedOutData?.quotaInMillions ?? '0.00',
                        rowType: 'quota',
                      },
                    ]}
                    getCellValue={(row, field) => {
                      if (field === 'label') return row[field];
                      return row[field] != null ? row[field] : '';
                    }}
                    getCellStyle={(row, field) => {
                      const baseStyle = {};
                      if (field === 'label') baseStyle.fontWeight = 600;
                      if (row.rowType === 'movedOut') {
                        baseStyle.backgroundColor = '#f5f5f5';
                        if (field === 'count' || field === 'acvInMillions') baseStyle.fontWeight = 600;
                      }
                      if (row.rowType === 'quota') {
                        baseStyle.backgroundColor = '#fff9c4';
                        if (field === 'acvInMillions') baseStyle.fontWeight = 600;
                      }
                      return baseStyle;
                    }}
                    rowKey="label"
                    hideSubHeaderRow={true}
                    maxWidth="34%"
                  />
                </Box>
              )}
              */}

              {/* Opportunities Moved Out of Forecast (Most Likely + Commit) — raw data, same as RegionalSalesReviewPackage */}
              <Box mb={3}>
                {/* <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: forecastMovedOutRawRows.length > 0 ? 70 : 0 }}>
                  Opportunities Moved Out of Forecast (Most Likely + Commit)
                </Typography> */}
                {forecastMovedOutRawRows.length === 0 ? (
                  <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                    No forecast opps moved out this week
                </Box>
                ) : (
                  <ResizableTable
                    columns={forecastMovedOutColumns}
                    rows={forecastMovedOutRowsWithTotal}
                    getCellValue={forecastGetCellValue}
                    getCellStyle={(row) => row.isTotal ? { fontWeight: 700, backgroundColor: '#f5f5f5' } : {}}
                    rowKey="Opp_ID"
                    hideSubHeaderRow={true}
                    maxWidth="100%"
                    onRowClick={() => {}}
                  />
                )}
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              {'Current Quarter – Best Case'}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isForecastBestCaseLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              {forecastBestCaseRows.length === 0 ? (
                <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                  No best case opps found
                </Box>
              ) : (
                <ResizableTable
                  columns={createForecastOppsColumns()}
                  rows={forecastBestCaseRows}
                  getCellValue={getCellValue}
                  getCellStyle={getCellStyle}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={() => {}}
                />
              )}
            </>
          )}
        </Paper>
      </Box>

      <Box mt={4}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.title}>
              {'Sales Forecast – Next Quarter'}
              {salesForecastNextQuarterData?.targetQuarter
                ? ` (${salesForecastNextQuarterData.targetQuarter})`
                : ''}
            </Typography>
            <span className={classes.updateFrequency}>
              {'Live Data'}
            </span>
          </Box>

          {isSalesForecastNextQuarterLoading ? (
            <Skeleton variant="rect" animation="wave" height={200} width="100%" />
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                <ResizableTable
                  columns={createSummaryTableColumns(currencySymbol)}
                  rows={salesForecastNextQuarterSummaryRows}
                  getCellValue={(row, field) => {
                    if (field === 'label') return row[field];
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row, field) => {
                    const baseStyle = {};
                    if (field === 'label') baseStyle.fontWeight = 600;
                    if (row.rowType === 'total') {
                      baseStyle.backgroundColor = '#f5f5f5';
                      if (field === 'count' || field === 'acvInMillions') baseStyle.fontWeight = 600;
                    }
                    if (row.rowType === 'quota') {
                      baseStyle.backgroundColor = '#fff9c4';
                      if (field === 'acvInMillions') baseStyle.fontWeight = 600;
                    }
                    return baseStyle;
                  }}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="34%"
                />
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                  Sales Forecast – Next Quarter
                  {salesForecastNextQuarterData?.targetQuarter
                    ? ` (${salesForecastNextQuarterData.targetQuarter})`
                    : ''}
                  {' – Opps in Forecast – Most Likely + Commit'}
                </Typography>
                {salesForecastNextQuarterOppsWithSummary.length === 0 ? (
                  <Box textAlign="center" py={4} style={{ fontStyle: 'italic', color: '#666' }}>
                    No forecast opportunities for next quarter
                  </Box>
                ) : (
                  <ResizableTable
                    columns={salesForecastNextQuarterColumns}
                    rows={salesForecastNextQuarterOppsWithSummary}
                    getCellValue={getNextQtrCellValue}
                    getCellStyle={getNextQtrCellStyle}
                    rowKey="Opp_ID"
                    hideSubHeaderRow={true}
                    maxWidth="100%"
                    onRowClick={() => {}}
                  />
                )}
              </Box>

              <Box mt={2}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                    <Typography variant="body2">New this week</Typography>
                  </div>
                </div>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default RegionalRSMSalesForecastSection;
